import { useQuery, type DocumentNode } from '@apollo/client';
import type { AUTHENTICATION_TYPES } from '@seek/apac-candidate-header-footer/types';
import { useMelwaysInfo, useMelwaysLink } from '@seek/melways-react';
import { type CountrySiteName, getZoneFromSite } from '@seek/melways-sites';
import { useTranslations } from '@vocab/react';
import {
  Box,
  Loader,
  Stack,
  Drawer,
  Dialog,
  Text,
  Actions,
  Button,
} from 'braid-design-system';
import { useState, type JSX } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';

import { NoJobs } from 'src/components/NoJobs/NoJobs';
import { SomethingWentWrong } from 'src/components/SomethingWentWrong/SomethingWentWrong';
import { ViewApplication } from 'src/components/ViewApplication/ViewApplication';
import { useAppConfig } from 'src/config/context';
import { MAX_ITEMS, JOB_TYPE } from 'src/const/app';
import { AppliedJobItem } from 'src/containers/AppliedJobItem/AppliedJobItem';
import { Edit } from 'src/containers/JobNotes/Edit';
import { SavedJobItem } from 'src/containers/SavedJobItem/SavedJobItem';
import { hasUnauthError } from 'src/graphql/links';
import { useMemoScrollPosition } from 'src/hooks/useMemoScrollPosition';
import { usePagination } from 'src/hooks/usePagination';
import { type APPLIED_JOB_SORT, useSort } from 'src/hooks/useSort';
import { useTracking } from 'src/hooks/useTracking';
import { analyticsClient } from 'src/modules/analytics/analytics';
import type {
  AppliedJob,
  GetAppliedJobsQuery,
  GetSavedJobsQuery,
  SavedJob,
} from 'src/types/graphql';

import translations from './JobsContainer.vocab';
import JobsContainerPaginationView from './JobsContainerPaginationView';
import JobsContainerSortView from './JobsContainerSortView';
import { GET_APPLIED_JOBS, GET_SAVED_JOBS } from './queries.graphql';

interface Params {
  type: typeof JOB_TYPE.applied | typeof JOB_TYPE.saved;
  authenticationStatus: AUTHENTICATION_TYPES;
}

const QUERY_MAP: Record<Params['type'], DocumentNode> = {
  applied: GET_APPLIED_JOBS,
  saved: GET_SAVED_JOBS,
};

const ITEM_MAP: Record<Params['type'], (props: any) => JSX.Element> = {
  applied: AppliedJobItem,
  saved: SavedJobItem,
};

type GraphQLResponse = GetAppliedJobsQuery & GetSavedJobsQuery;

const JobsContainer = ({ type, authenticationStatus }: Params) => {
  const { t } = useTranslations(translations);
  const { BRAND: brand } = useAppConfig();
  const { locale, site } = useMelwaysInfo();
  const { currentSort, setCurrentSort, sortQueryVar } = useSort();

  analyticsClient.setState({
    siteSection: type === JOB_TYPE.applied ? 'apply' : 'discover',
  });

  const { data, loading, error } = useQuery<GraphQLResponse>(QUERY_MAP[type], {
    variables: {
      first: MAX_ITEMS,
      locale,
      timezone:
        // eslint-disable-next-line new-cap
        Intl.DateTimeFormat().resolvedOptions().timeZone ||
        'Australia/Melbourne',
      zone: getZoneFromSite(site as CountrySiteName),
      sortBy: sortQueryVar,
    },
  });
  const connection = data?.viewer?.appliedJobs || data?.viewer?.savedJobs;
  const nodes = (connection?.edges.map(({ node }) => node) || []) as Array<
    SavedJob & AppliedJob
  >;

  const { currentNodes, currentPage, setCurrentPage, totalPages } =
    usePagination(nodes);

  const paginationClick = (page: number) => {
    setCurrentPage(page);
    analyticsClient.trackLink({
      eventName: 'navigation_pressed',
      linkPosition: 'pagination',
      linkText: page,
    });
  };

  const onSortMenuOpen = () => {
    analyticsClient.trackLink({
      eventName: 'sort_by_pressed',
    });
  };

  const handleSortChange = (sort: APPLIED_JOB_SORT) => {
    setCurrentSort(sort);
    analyticsClient.trackLink({
      eventName: 'option_selection_pressed',
      sortMode: sort,
    });
  };

  useTracking(
    nodes as Array<SavedJob & AppliedJob>,
    type,
    authenticationStatus,
    type === 'applied' ? currentSort : '',
    loading,
  );
  useMemoScrollPosition(loading);

  // View Application Drawer
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { hash } = useLocation();
  const urlJobId = searchParams.get('jobId');
  const job = currentNodes.find((node) => node.id === urlJobId);
  const { isActive = true, notes } = job || {};
  const link = useMelwaysLink(
    `/${!isActive ? 'expired' : ''}job/${urlJobId}?ref=${type}`,
  );
  const isOpenView = hash === '#view' && Boolean(urlJobId) && Boolean(job);

  const handleOpenView = (jobId: string | null) => {
    searchParams.set('jobId', jobId || '');
    setSearchParams(searchParams);
    navigate(`${location.pathname}${location.search}#view`);
  };

  const handleCloseView = () => {
    searchParams.delete('jobId');
    setSearchParams(searchParams);
    navigate(`${location.pathname}${location.search}`);
  };

  // Notes Drawer
  const isOpenNotes = hash === '#notes' && Boolean(urlJobId) && Boolean(job);
  const [drawerNotesIsDirty, setDrawerNotesIsDirty] = useState(false);
  const [showDiscardDialog, setShowDiscardDialog] = useState(false);

  const resetNotes = () => {
    searchParams.delete('jobId');
    setSearchParams(searchParams);
    navigate(`${location.pathname}${location.search}`);
  };

  const handleOpenNotes = (jobId: string | null) => {
    searchParams.set('jobId', jobId || '');
    setSearchParams(searchParams);
    navigate(`${location.pathname}${location.search}#notes`);
  };

  const handleCloseNotes = () => {
    if (!drawerNotesIsDirty) {
      resetNotes();
    } else {
      setShowDiscardDialog(true);
      return false;
    }
  };

  const handleCancel = () => {
    setDrawerNotesIsDirty(false);
    resetNotes();
  };

  const getIsDirty = () => {
    setDrawerNotesIsDirty(true);
  };

  const handleDiscard = () => {
    setDrawerNotesIsDirty(false);
    setShowDiscardDialog(false);
    resetNotes();
  };

  if (error && !hasUnauthError(error.graphQLErrors)) {
    return <SomethingWentWrong />;
  }

  if (loading) {
    return (
      <Box justifyContent="center" display="flex" padding="xlarge">
        <Loader size="large" />
      </Box>
    );
  }

  const titles = {
    [JOB_TYPE.applied]: t('Applied jobs', { brand }),
    [JOB_TYPE.saved]: t('Saved jobs', { brand }),
  };

  if (nodes.length === 0) {
    return (
      <>
        <Helmet title={titles[type]} />
        <NoJobs type={type.toUpperCase()} />
      </>
    );
  }

  const Component = ITEM_MAP[type];

  return (
    <>
      <Helmet title={titles[type]} />
      {type === 'applied' && (
        <JobsContainerSortView
          totalJobs={String(nodes.length)}
          onSortMenuOpen={onSortMenuOpen}
          handleSortChange={handleSortChange}
          currentSort={currentSort}
        />
      )}
      <Stack space="large">
        {currentNodes.map((node, index) => (
          <Component
            key={node.id}
            job={node}
            index={index}
            openView={handleOpenView}
            openNotes={handleOpenNotes}
          />
        ))}
      </Stack>
      <JobsContainerPaginationView
        onClick={paginationClick}
        currentPage={currentPage}
        totalPages={totalPages}
        type={type}
      />
      <>
        {/* view */}
        <Drawer
          id={`view-application-${urlJobId}`}
          title={t('View application')}
          width="medium"
          open={isOpenView}
          onClose={handleCloseView}
        >
          {job && (
            <ViewApplication
              index={Number(urlJobId)}
              job={job as AppliedJob}
              link={link}
            />
          )}
        </Drawer>
        {/* notes */}
        <Drawer
          id={`drawer-notes-${urlJobId}`}
          title={Boolean(notes) ? t('Edit notes') : t('Add notes')}
          width="small"
          open={isOpenNotes}
          onClose={handleCloseNotes}
        >
          {job && (
            <Edit
              index={Number(urlJobId)}
              job={job}
              setView={handleCancel}
              notes={notes || ''}
              getIsDirty={getIsDirty}
              type={type}
            />
          )}
        </Drawer>
        <Dialog
          id={`dialog-discard-${urlJobId}`}
          title={t('Discard changes')}
          open={showDiscardDialog}
          onClose={() => setShowDiscardDialog(false)}
          closeLabel={t('Discard')}
          width="xsmall"
        >
          <Text>{t('Are you sure you want to leave without saving?')}</Text>
          <Actions>
            <Button
              id={`dialog-discard-button-${urlJobId}`}
              tone="critical"
              variant="ghost"
              onClick={handleDiscard}
            >
              {t('Discard')}
            </Button>
            <Button
              id={`dialog-discard-cancel-button-${urlJobId}`}
              tone="formAccent"
              variant="transparent"
              onClick={() => setShowDiscardDialog(false)}
            >
              {t('Cancel')}
            </Button>
          </Actions>
        </Dialog>
      </>
    </>
  );
};

export default JobsContainer;
